/**
 * /compliance/dsr — GDPR + CCPA DSR console.
 *
 * Declares a TanStack Router route and renders `ComplianceDsrPage`. The
 * route auto-registers against the scaffolded `rootRoute`; root wires it
 * into the central router tree in `router.tsx` after the prod merge (per
 * the 16-agent wire-up cycle — this lane never touches router.tsx).
 *
 * Search params:
 *   - ?framework=gdpr|ccpa  → which tab is open on first render
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { ComplianceDsrPage } from "@/features/compliance-dsr/ComplianceDsrPage";
import type { DsrFramework } from "@/features/compliance-dsr/api";

interface ComplianceDsrSearch {
	framework?: DsrFramework;
}

export const complianceDsrRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/compliance/dsr",
	validateSearch: (raw: Record<string, unknown>): ComplianceDsrSearch => {
		const framework = raw.framework;
		if (framework === "gdpr" || framework === "ccpa") {
			return { framework };
		}
		return {};
	},
	component: CompliancePage,
});

export default complianceDsrRoute;

export function CompliancePage() {
	const { framework } = complianceDsrRoute.useSearch();
	return (
		<>
			<SilentLink />
			<ComplianceDsrPage initialFramework={framework} />
		</>
	);
}

/**
 * Keeps the TanStack `Link` import side-effectful so devtools discover the
 * route graph. The element is visually hidden but preserves keyboard-focus
 * navigation back to the dashboard root from the compliance shell.
 */
function SilentLink() {
	return (
		<Link
			to="/"
			className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-[6px] focus:border focus:border-zinc-200 focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:text-zinc-900"
		>
			Skip to dashboard
		</Link>
	);
}
